import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  LLM_PROVIDERS,
  type AgentWizardData,
  type LLMProvider,
} from '@/schemas/agent-wizard'
import type { LLMProviderValue } from '@/schemas/llm-api-key'
import { ApiKeySelector } from './api-key-selector'
import { Cpu, Thermometer, Hash } from 'lucide-react'

export function StepLLMConfig() {
  const { t } = useTranslation('agents')
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgentWizardData>()

  const selectedProvider = watch('provider') as LLMProvider | null
  const temperature = watch('temperature')

  // Get models for selected provider
  const availableModels = selectedProvider
    ? LLM_PROVIDERS[selectedProvider]?.models ?? []
    : []

  // Reset model when provider changes
  const handleProviderChange = (value: string) => {
    setValue('provider', value)
    setValue('model', null) // Reset model selection
  }

  return (
    <div className="space-y-6">
      {/* Provider and Model Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Provider Select */}
        <div className="space-y-2">
          <Label className="text-gray-300 flex items-center gap-2">
            <Cpu className="h-4 w-4" />
            {t('wizard.llmConfig.provider')}
          </Label>
          <Controller
            name="provider"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={handleProviderChange}
                value={field.value ?? undefined}
              >
                <SelectTrigger
                  className={cn(
                    'bg-white/5 border-gray-700/50 text-white',
                    'focus:border-indigo-500 focus:ring-indigo-500/20',
                    errors.provider && 'border-red-500'
                  )}
                >
                  <SelectValue placeholder={t('wizard.llmConfig.selectProvider')} />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {Object.entries(LLM_PROVIDERS).map(([key, provider]) => (
                    <SelectItem
                      key={key}
                      value={key}
                      className="text-gray-200 focus:bg-indigo-500/20 focus:text-white"
                    >
                      {provider.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.provider && (
            <p className="text-sm text-red-400">{errors.provider.message}</p>
          )}
        </div>

        {/* Model Select */}
        <div className="space-y-2">
          <Label className="text-gray-300 flex items-center gap-2">
            <Cpu className="h-4 w-4" />
            {t('wizard.llmConfig.model')}
          </Label>
          <Controller
            name="model"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value ?? undefined}
                disabled={!selectedProvider}
              >
                <SelectTrigger
                  className={cn(
                    'bg-white/5 border-gray-700/50 text-white',
                    'focus:border-indigo-500 focus:ring-indigo-500/20',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    errors.model && 'border-red-500'
                  )}
                >
                  <SelectValue
                    placeholder={
                      selectedProvider
                        ? t('wizard.llmConfig.selectModel')
                        : t('wizard.llmConfig.selectProviderFirst')
                    }
                  />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {availableModels.map((model) => (
                    <SelectItem
                      key={model.value}
                      value={model.value}
                      className="text-gray-200 focus:bg-indigo-500/20 focus:text-white"
                    >
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.model && (
            <p className="text-sm text-red-400">{errors.model.message}</p>
          )}
        </div>
      </div>

      {/* Temperature Slider */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-gray-300 flex items-center gap-2">
            <Thermometer className="h-4 w-4" />
            {t('wizard.llmConfig.temperature')}
          </Label>
          <span className="text-sm font-mono text-indigo-400">
            {temperature?.toFixed(1) ?? '0.7'}
          </span>
        </div>
        <Controller
          name="temperature"
          control={control}
          render={({ field }) => (
            <input
              type="range"
              min={0}
              max={2}
              step={0.1}
              value={field.value ?? 0.7}
              onChange={(e) => field.onChange(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-full appearance-none cursor-pointer accent-indigo-500"
            />
          )}
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>{t('wizard.llmConfig.temperaturePrecise')}</span>
          <span>{t('wizard.llmConfig.temperatureBalanced')}</span>
          <span>{t('wizard.llmConfig.temperatureCreative')}</span>
        </div>
        <p className="text-xs text-gray-500">
          {t('wizard.llmConfig.temperatureInfo')}
        </p>
      </div>

      {/* Max Tokens Input */}
      <div className="space-y-2">
        <Label htmlFor="max_tokens" className="text-gray-300 flex items-center gap-2">
          <Hash className="h-4 w-4" />
          {t('wizard.llmConfig.maxTokens')}
        </Label>
        <Controller
          name="max_tokens"
          control={control}
          render={({ field }) => (
            <Input
              id="max_tokens"
              type="number"
              min={1}
              max={128000}
              value={field.value ?? 2048}
              onChange={(e) => field.onChange(parseInt(e.target.value) || 2048)}
              className={cn(
                'bg-white/5 border-gray-700/50 text-white',
                'focus:border-indigo-500 focus:ring-indigo-500/20',
                errors.max_tokens && 'border-red-500'
              )}
            />
          )}
        />
        {errors.max_tokens && (
          <p className="text-sm text-red-400">{errors.max_tokens.message}</p>
        )}
        <p className="text-xs text-gray-500">
          {t('wizard.llmConfig.maxTokensHelper')}
        </p>
      </div>

      {/* API Key Selection */}
      <ApiKeySelector provider={selectedProvider as LLMProviderValue | null} />

      {/* Summary Card */}
      {selectedProvider && (
        <div className="mt-6 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/30">
          <p className="text-sm font-medium text-indigo-300 mb-2">
            {t('wizard.llmConfig.summary')}
          </p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-gray-400">{t('wizard.llmConfig.provider')}:</div>
            <div className="text-white">
              {selectedProvider ? LLM_PROVIDERS[selectedProvider].label : '-'}
            </div>
            <div className="text-gray-400">{t('wizard.llmConfig.model')}:</div>
            <div className="text-white">{watch('model') || '-'}</div>
            <div className="text-gray-400">{t('wizard.llmConfig.temperature')}:</div>
            <div className="text-white">{temperature?.toFixed(1) ?? '0.7'}</div>
            <div className="text-gray-400">{t('wizard.llmConfig.maxTokens')}:</div>
            <div className="text-white">{watch('max_tokens') ?? 2048}</div>
          </div>
        </div>
      )}
    </div>
  )
}
